'use strict';

/**
 * Bulk File Upload — view (ProjectWorkspace ▸ Data ▸ Bulk upload files).
 *
 * A drop zone + "Choose files" front end that stages many files into an editable grid (Name + Designated Date per
 * row, with the observation type auto-detected from the MIME), then uploads them all — one Observation per file —
 * with a per-row progress bar and a byte-weighted overall bar. Rows stay editable before upload (values are baked
 * into each observation) and after (a change PUTs the created observation). Embeds into the workspace Data tab via
 * the shared render()-override / openInto / exitEmbed pattern; the provider owns the row data + upload pipeline and
 * pokes per-row/overall progress straight into the DOM so inputs keep focus mid-upload.
 */

const libPictView = require('pict-view');

const html = String.raw;
const fEsc = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[pChar]));
const fAttr = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));

const TYPE_ICONS = { Image: 'fa-file-image', Video: 'fa-file-video', Audio: 'fa-file-audio', File: 'fa-file-lines' };
const fTypeIcon = (pType) => TYPE_ICONS[pType] || 'fa-file-lines';

const fBytes = (pBytes) =>
{
	const tmpBytes = Number(pBytes) || 0;
	if (tmpBytes < 1024) { return `${tmpBytes} B`; }
	const tmpUnits = [ 'KB', 'MB', 'GB' ];
	let tmpValue = tmpBytes / 1024;
	let tmpUnit = 0;
	while (tmpValue >= 1024 && tmpUnit < tmpUnits.length - 1) { tmpValue /= 1024; tmpUnit++; }
	return `${tmpValue.toFixed(tmpValue >= 10 ? 0 : 1)} ${tmpUnits[tmpUnit]}`;
};

const GRID_MOUNT = '#BulkUpload-Rows';
const OVERALL_MOUNT = '#BulkUpload-Overall';

class BulkUpload extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._embedDestination = null;
	}

	get provider() { return this.pict.providers['BulkUpload']; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType, pDuration)
	{
		if (this.modal && (typeof this.modal.toast === 'function'))
		{
			const tmpOptions = { type: pType || 'info' };
			if (pDuration !== undefined) { tmpOptions.duration = pDuration; }
			this.modal.toast(pMessage, tmpOptions);
		}
	}

	/** Render into the embed host when embedded (provider `_render()` repaints land in the Data tab). */
	render(pRenderableHash, pRenderDestinationAddress, pTemplateRecordAddress, pRootRenderable)
	{
		const tmpDestination = (typeof pRenderDestinationAddress === 'string') ? pRenderDestinationAddress : (this._embedDestination || undefined);
		return super.render(pRenderableHash, tmpDestination, pTemplateRecordAddress, pRootRenderable);
	}

	/** Embed for a fixed project. The provider clears staged rows when the project changes. */
	openInto(pIDProject, pHostSelector)
	{
		this._embedDestination = pHostSelector;
		if (this.provider) { this.provider.setProject(pIDProject); }
		this.render();
	}

	exitEmbed() { this._embedDestination = null; }

	onBeforeRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.BulkUpload) || {};
		const tmpProvider = this.provider;
		const tmpRows = tmpProvider ? tmpProvider.rows : [];
		tmpState.HasRows = tmpRows.length > 0;
		tmpState.Uploading = !!(tmpProvider && tmpProvider.isUploading);
		tmpState.CanUpload = !!(tmpProvider && tmpProvider.canUpload());
		const tmpTotalBytes = tmpRows.reduce((pSum, pRow) => pSum + (pRow.Size || 0), 0);
		tmpState.CountLabel = `${tmpRows.length} file${tmpRows.length === 1 ? '' : 's'} · ${fBytes(tmpTotalBytes)}`;
		tmpState.EmbedClass = this._embedDestination ? 'bu-embedded' : '';
		// Mutually-exclusive body sections via the single-element-array slot trick.
		tmpState.NoPermSlot = !tmpState.CanUpload ? [ tmpState ] : [];
		tmpState.ToolbarSlot = (tmpState.CanUpload && tmpState.HasRows) ? [ tmpState ] : [];
		tmpState.GridSlot = (tmpState.CanUpload && tmpState.HasRows) ? [ tmpState ] : [];
		this.pict.AppData.BulkUpload = tmpState;
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.BulkUpload) || {};
		if (tmpState.CanUpload && tmpState.HasRows) { this.renderGrid(); this.paintOverall(); }
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable);
	}

	// --- File intake ---------------------------------------------------------------------------------

	/** Open the OS multi-file picker. */
	chooseFiles()
	{
		if (this.provider && !this.provider.canUpload()) { return; }
		let tmpInput = document.getElementById('BulkUpload-FileInput');
		if (!tmpInput)
		{
			tmpInput = document.createElement('input');
			tmpInput.type = 'file';
			tmpInput.id = 'BulkUpload-FileInput';
			tmpInput.multiple = true;
			tmpInput.style.display = 'none';
			document.body.appendChild(tmpInput);
		}
		tmpInput.value = '';
		tmpInput.onchange = () =>
		{
			const tmpFiles = tmpInput.files;
			tmpInput.onchange = null;
			if (tmpFiles && tmpFiles.length) { this.provider.addFiles(tmpFiles); }
		};
		tmpInput.click();
	}

	onDragOver(pEvent)
	{
		if (pEvent) { pEvent.preventDefault(); if (pEvent.dataTransfer) { pEvent.dataTransfer.dropEffect = 'copy'; } }
		const tmpZone = document.getElementById('BulkUpload-Drop');
		if (tmpZone) { tmpZone.classList.add('is-dragover'); }
	}

	onDragLeave(pEvent)
	{
		if (pEvent) { pEvent.preventDefault(); }
		const tmpZone = document.getElementById('BulkUpload-Drop');
		if (tmpZone) { tmpZone.classList.remove('is-dragover'); }
	}

	onDrop(pEvent)
	{
		if (pEvent) { pEvent.preventDefault(); }
		const tmpZone = document.getElementById('BulkUpload-Drop');
		if (tmpZone) { tmpZone.classList.remove('is-dragover'); }
		if (this.provider && !this.provider.canUpload()) { return; }
		const tmpFiles = pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files;
		if (tmpFiles && tmpFiles.length) { this.provider.addFiles(tmpFiles); }
	}

	// --- Grid ----------------------------------------------------------------------------------------

	renderGrid()
	{
		const tmpMount = this.pict.ContentAssignment.getElement(GRID_MOUNT);
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpRows = this.provider ? this.provider.rows : [];
		this.pict.ContentAssignment.assignContent(GRID_MOUNT, tmpRows.map((pRow) => this._rowHTML(pRow)).join(''));
	}

	_statusCellHTML(pRow)
	{
		if (pRow.Status === 'uploading')
		{
			return `<div class="bu-prog"><div class="bu-prog-fill" style="width:${Math.max(0, Math.min(100, pRow.Progress || 0))}%"></div></div>`
				+ `<span class="bu-prog-pct">${Math.round(pRow.Progress || 0)}%</span>`;
		}
		if (pRow.Status === 'done') { return `<span class="bu-st bu-st--done"><i class="fas fa-circle-check"></i> Uploaded</span>`; }
		if (pRow.Status === 'error') { return `<span class="bu-st bu-st--error" title="${fAttr(pRow.Message)}"><i class="fas fa-triangle-exclamation"></i> ${fEsc(pRow.Message || 'Failed')}</span>`; }
		return `<span class="bu-st bu-st--staged">Ready</span>`;
	}

	_rowHTML(pRow)
	{
		const tmpUID = fAttr(pRow.UID);
		const tmpBusy = (pRow.Status === 'uploading');
		const tmpDisabled = tmpBusy ? 'disabled' : '';
		// Staged/errored rows can be removed (from the list only); a done row is committed — no remove.
		const tmpRemovable = (pRow.Status === 'staged') || (pRow.Status === 'error');
		return html`<tr class="bu-row" data-uid="${tmpUID}" data-status="${fAttr(pRow.Status)}">`
			+ `<td class="bu-ic"><i class="fas ${fTypeIcon(pRow.Type)}"></i></td>`
			+ `<td class="bu-cell-name"><input type="text" class="bu-name" value="${fAttr(pRow.Name)}" ${tmpDisabled} oninput="_Pict.views.BulkUpload.onNameInput('${tmpUID}', this.value)" /></td>`
			+ `<td class="bu-cell-date"><input type="date" class="bu-date" value="${fAttr(pRow.DateISO)}" ${tmpDisabled} onchange="_Pict.views.BulkUpload.onDateInput('${tmpUID}', this.value)" /></td>`
			+ `<td class="bu-cell-type"><span class="bu-type bu-type--${fAttr(pRow.Type)}">${fEsc(pRow.Type)}</span></td>`
			+ `<td class="bu-size">${fEsc(fBytes(pRow.Size))}</td>`
			+ `<td class="bu-status">${this._statusCellHTML(pRow)}</td>`
			+ `<td class="bu-actions">${tmpRemovable ? `<button type="button" class="bu-remove" title="Remove from list" onclick="_Pict.views.BulkUpload.removeRow('${tmpUID}')"><i class="fas fa-xmark"></i></button>` : ''}</td>`
			+ `</tr>`;
	}

	/** Repaint a single row's status + input-disabled state in place (no full re-render → inputs keep focus). */
	paintRow(pUID)
	{
		const tmpRow = this.provider ? this.provider._findRow(pUID) : null;
		if (!tmpRow) { return; }
		const tmpTR = document.querySelector(`.bu-row[data-uid="${(window.CSS && CSS.escape) ? CSS.escape(pUID) : pUID}"]`);
		if (!tmpTR) { return; }
		tmpTR.setAttribute('data-status', tmpRow.Status);
		const tmpStatus = tmpTR.querySelector('.bu-status');
		if (tmpStatus) { tmpStatus.innerHTML = this._statusCellHTML(tmpRow); }
		const tmpBusy = (tmpRow.Status === 'uploading');
		const tmpName = tmpTR.querySelector('.bu-name');
		const tmpDate = tmpTR.querySelector('.bu-date');
		if (tmpName) { tmpName.disabled = tmpBusy; }
		if (tmpDate) { tmpDate.disabled = tmpBusy; }
		const tmpActions = tmpTR.querySelector('.bu-actions');
		if (tmpActions && !((tmpRow.Status === 'staged') || (tmpRow.Status === 'error'))) { tmpActions.innerHTML = ''; }
	}

	/** Repaint the overall progress bar + counts. */
	paintOverall()
	{
		const tmpMount = document.getElementById('BulkUpload-Overall');
		if (!tmpMount || !this.provider) { return; }
		const tmpStats = this.provider.overallStats();
		const tmpParts = [ `${tmpStats.done} of ${tmpStats.total} uploaded` ];
		if (tmpStats.uploading) { tmpParts.push(`${tmpStats.uploading} in progress`); }
		if (tmpStats.failed) { tmpParts.push(`${tmpStats.failed} failed`); }
		tmpMount.innerHTML = `<div class="bu-overall-bar"><div class="bu-overall-fill" style="width:${tmpStats.percent}%"></div></div>`
			+ `<div class="bu-overall-label"><span>${tmpStats.percent}%</span><span>${fEsc(tmpParts.join(' · '))}</span></div>`;
	}

	/** Re-render the toolbar's disabled state (called when an upload run starts/ends). */
	paintToolbar()
	{
		const tmpProvider = this.provider;
		const tmpBusy = !!(tmpProvider && tmpProvider.isUploading);
		const tmpChoose = document.getElementById('BulkUpload-Choose');
		const tmpClear = document.getElementById('BulkUpload-Clear');
		const tmpUpload = document.getElementById('BulkUpload-Start');
		if (tmpChoose) { tmpChoose.disabled = tmpBusy; }
		if (tmpClear) { tmpClear.disabled = tmpBusy; }
		if (tmpUpload)
		{
			tmpUpload.disabled = tmpBusy;
			tmpUpload.innerHTML = tmpBusy
				? '<i class="fas fa-spinner fa-spin"></i> Uploading…'
				: '<i class="fas fa-cloud-arrow-up"></i> Upload';
		}
		const tmpZone = document.getElementById('BulkUpload-Drop');
		if (tmpZone) { tmpZone.classList.toggle('is-disabled', tmpBusy); }
	}

	// --- Row edits + actions -------------------------------------------------------------------------

	onNameInput(pUID, pValue) { if (this.provider) { this.provider.setRowName(pUID, pValue); } }
	onDateInput(pUID, pValue) { if (this.provider) { this.provider.setRowDate(pUID, pValue); } }
	removeRow(pUID) { if (this.provider) { this.provider.removeRow(pUID); } }

	async clearAll()
	{
		if (!this.provider || this.provider.isUploading) { return; }
		const tmpHasDone = this.provider.rows.some((pRow) => pRow.Status === 'done');
		if (tmpHasDone && this.modal && this.modal.confirm)
		{
			const tmpOk = await this.modal.confirm('Clear the list? Uploaded observations stay on the project — this only clears the grid.', { title: 'Clear list', confirmLabel: 'Clear', cancelLabel: 'Cancel' });
			if (!tmpOk) { return; }
		}
		this.provider.clearAll();
	}

	startUpload() { if (this.provider) { this.provider.uploadAllAsync(); } }

}

module.exports = BulkUpload;
module.exports.default_configuration = {
	ViewIdentifier: 'BulkUpload',
	DefaultRenderable: 'BulkUploadWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.bu-wrap { max-width: 1000px; margin: 0 auto; padding: 1.5rem 1.25rem 4rem; }
		.bu-head { display: flex; align-items: flex-start; gap: 0.85rem; margin-bottom: 1.1rem; }
		.bu-head-ic { flex: none; width: 2.4rem; height: 2.4rem; border-radius: 10px; display: grid; place-items: center; background: var(--theme-color-background-selected, #e3edfb); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.1rem; }
		.bu-title { font-size: 1.5rem; font-weight: 700; margin: 0; line-height: 1.15; color: var(--theme-color-text-primary, #1f2733); }
		.bu-sub { margin: 0.15rem 0 0; color: var(--theme-color-text-secondary, #5b6673); font-size: 0.92rem; }

		.bu-noperm { display: flex; align-items: center; gap: 0.6rem; padding: 1rem 1.1rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 10px; background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-secondary, #5b6673); font-size: 0.9rem; }
		.bu-noperm i { color: var(--theme-color-text-muted, #8a94a3); }

		/* Drop zone */
		.bu-drop { border: 2px dashed var(--theme-color-border-default, #d7dce3); border-radius: 12px; padding: 1.6rem 1rem; text-align: center; background: var(--theme-color-background-panel, #fff); transition: border-color 0.12s ease, background 0.12s ease; }
		.bu-drop.is-dragover { border-color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-selected, #e3edfb); }
		.bu-drop.is-disabled { opacity: 0.55; pointer-events: none; }
		.bu-drop-ic { font-size: 1.7rem; color: var(--theme-color-brand-primary, #156dd1); opacity: 0.8; margin-bottom: 0.4rem; }
		.bu-drop-main { font-size: 0.98rem; color: var(--theme-color-text-primary, #1f2733); }
		.bu-drop-btn { display: inline-flex; align-items: center; gap: 0.4rem; margin: 0 0.15rem; font: inherit; font-size: 0.95rem; font-weight: 600; cursor: pointer; border: none; background: transparent; color: var(--theme-color-brand-primary, #156dd1); text-decoration: underline; padding: 0; }
		.bu-drop-sub { margin: 0.35rem 0 0; font-size: 0.82rem; color: var(--theme-color-text-muted, #8a94a3); }

		/* Toolbar + overall */
		.bu-toolbar { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; margin: 1rem 0 0.35rem; }
		.bu-count { font-size: 0.88rem; color: var(--theme-color-text-secondary, #5b6673); }
		.bu-spacer { flex: 1 1 auto; }
		.bu-btn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; cursor: pointer; padding: 0.5rem 0.9rem; border-radius: 8px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.bu-btn:hover:not([disabled]) { background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-primary, #1f2733); }
		.bu-btn[disabled] { opacity: 0.55; cursor: default; }
		.bu-btn-primary { border-color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-brand-primary, #156dd1); color: #fff; }
		.bu-btn-primary:hover:not([disabled]) { background: var(--theme-color-brand-primary-hover, #1160bd); color: #fff; }
		.bu-overall { margin: 0.35rem 0 0.6rem; }
		.bu-overall-bar { height: 8px; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); overflow: hidden; }
		.bu-overall-fill { height: 100%; background: var(--theme-color-brand-primary, #156dd1); border-radius: 999px; width: 0; transition: width 0.18s ease; }
		.bu-overall-label { display: flex; justify-content: space-between; margin-top: 0.3rem; font-size: 0.78rem; color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; }

		/* Grid */
		.bu-tablewrap { border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 12px; overflow: hidden; background: var(--theme-color-background-panel, #fff); }
		.bu-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
		.bu-table thead th { text-align: left; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); padding: 0.55rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-secondary, #f7f8fa); }
		.bu-table tbody td { padding: 0.4rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); vertical-align: middle; }
		.bu-table tbody tr:last-child td { border-bottom: none; }
		.bu-row[data-status="error"] { background: rgba(207, 91, 91, 0.06); }
		.bu-row[data-status="done"] .bu-name, .bu-row[data-status="done"] .bu-date { border-color: transparent; background: transparent; }
		.bu-ic { width: 1.6rem; color: var(--theme-color-text-secondary, #5b6673); }
		.bu-cell-name { width: 40%; }
		.bu-name { width: 100%; font: inherit; font-size: 0.88rem; padding: 0.35rem 0.5rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 6px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.bu-date { font: inherit; font-size: 0.85rem; padding: 0.32rem 0.45rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 6px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.bu-name:focus, .bu-date:focus { outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.bu-name[disabled], .bu-date[disabled] { opacity: 0.7; }
		.bu-type { display: inline-block; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.12rem 0.4rem; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-secondary, #5b6673); }
		.bu-type--Image { background: #e8f1fb; color: #1c6bc4; }
		.bu-type--Video { background: #f2e9fb; color: #7a3fc0; }
		.bu-type--Audio { background: #e7f6ee; color: #2f8f57; }
		.bu-type--File { background: #eef0f3; color: #55606e; }
		.bu-size { color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; white-space: nowrap; }
		.bu-status { width: 9.5rem; }
		.bu-prog { display: inline-block; vertical-align: middle; width: 6.5rem; height: 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); overflow: hidden; }
		.bu-prog-fill { height: 100%; background: var(--theme-color-brand-primary, #156dd1); border-radius: 999px; width: 0; transition: width 0.15s ease; }
		.bu-prog-pct { margin-left: 0.4rem; font-size: 0.76rem; color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; }
		.bu-st { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.8rem; }
		.bu-st--staged { color: var(--theme-color-text-muted, #8a94a3); }
		.bu-st--done { color: var(--theme-color-status-success, #3a9e5c); }
		.bu-st--error { color: var(--theme-color-status-error, #cf5b5b); max-width: 12rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.bu-actions { width: 2rem; text-align: right; }
		.bu-remove { border: none; background: transparent; cursor: pointer; color: var(--theme-color-text-muted, #8a94a3); padding: 0.25rem 0.4rem; border-radius: 6px; }
		.bu-remove:hover { color: var(--theme-color-status-error, #cf5b5b); background: rgba(207, 91, 91, 0.12); }

		/* Embedded in the ProjectWorkspace Data tab: shed the standalone page framing + header. */
		.section.bu-embedded { padding: 0; }
		.bu-embedded .bu-wrap { max-width: none; margin: 0; padding: 0; }
		.bu-embedded .bu-head { display: none; }
	`,

	Templates:
	[
		{
			Hash: 'BulkUpload-Template',
			Template: html`
				<section class="section {~D:AppData.BulkUpload.EmbedClass~}">
					<div class="bu-wrap">
						<div class="bu-head">
							<span class="bu-head-ic"><i class="fas fa-cloud-arrow-up"></i></span>
							<div>
								<h1 class="bu-title">Bulk upload files</h1>
								<p class="bu-sub">Add many files at once — each becomes an observation on this project.</p>
							</div>
						</div>
						{~TS:BulkUpload-NoPerm:AppData.BulkUpload.NoPermSlot~}
						<div id="BulkUpload-Drop" class="bu-drop" ondragover="_Pict.views.BulkUpload.onDragOver(event)" ondragleave="_Pict.views.BulkUpload.onDragLeave(event)" ondrop="_Pict.views.BulkUpload.onDrop(event)">
							<div class="bu-drop-ic"><i class="fas fa-cloud-arrow-up"></i></div>
							<div class="bu-drop-main">Drag files here, or <button type="button" id="BulkUpload-Choose" class="bu-drop-btn" onclick="_Pict.views.BulkUpload.chooseFiles()">choose files</button></div>
							<p class="bu-drop-sub">Images become image observations; audio → audio; video → video; everything else → a file observation. You can edit each name &amp; date below before uploading.</p>
						</div>
						{~TS:BulkUpload-Toolbar:AppData.BulkUpload.ToolbarSlot~}
						{~TS:BulkUpload-Grid:AppData.BulkUpload.GridSlot~}
					</div>
				</section>`,
		},
		{ Hash: 'BulkUpload-NoPerm', Template: html`<div class="bu-noperm"><i class="fas fa-lock"></i> Adding observations to this project needs the “Observation create” permission. You can view this screen, but uploading is disabled.</div>` },
		{
			Hash: 'BulkUpload-Toolbar',
			Template: html`
				<div class="bu-toolbar">
					<span class="bu-count">{~D:AppData.BulkUpload.CountLabel~}</span>
					<span class="bu-spacer"></span>
					<button type="button" id="BulkUpload-Clear" class="bu-btn" onclick="_Pict.views.BulkUpload.clearAll()"><i class="fas fa-eraser"></i> Clear</button>
					<button type="button" id="BulkUpload-Start" class="bu-btn bu-btn-primary" onclick="_Pict.views.BulkUpload.startUpload()"><i class="fas fa-cloud-arrow-up"></i> Upload</button>
				</div>
				<div id="BulkUpload-Overall" class="bu-overall"></div>`,
		},
		{
			Hash: 'BulkUpload-Grid',
			Template: html`
				<div class="bu-tablewrap">
					<table class="bu-table">
						<thead><tr><th></th><th>Name</th><th>Designated date</th><th>Type</th><th>Size</th><th>Status</th><th></th></tr></thead>
						<tbody id="BulkUpload-Rows"></tbody>
					</table>
				</div>`,
		},
	],

	Renderables:
	[
		{ RenderableHash: 'BulkUploadWrap', TemplateHash: 'BulkUpload-Template', DestinationAddress: '#PlatformConfiguration-Application-Container' },
	],
};
